/**
 * MSAL Configuration
 * 
 * Feature: 033-auth-complete
 * Description: Microsoft Authentication Library configuration for Entra ID
 * Constitution: v1.11.0
 */

import { Configuration, PublicClientApplication } from '@azure/msal-browser';

/**
 * MSAL configuration
 * Loads from environment variables
 */
export const msalConfig: Configuration = {
  auth: {
    clientId: import.meta.env.VITE_AZURE_AD_CLIENT_ID || import.meta.env.VITE_ENTRA_CLIENT_ID || '',
    authority: import.meta.env.VITE_ENTRA_AUTHORITY || `https://login.microsoftonline.com/${import.meta.env.VITE_AZURE_AD_TENANT_ID}`,
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
    navigateToLoginRequestUrl: false,
  },
  cache: {
    cacheLocation: 'sessionStorage', // sessionStorage persists across redirects (required for redirect flow)
    storeAuthStateInCookie: false, // Don't use cookies
  },
  system: {
    loggerOptions: {
      logLevel: import.meta.env.DEV ? 3 : 0, // Verbose in dev, off in production
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;
        switch (level) {
          case 0: // Error
            console.error('[MSAL]', message);
            break;
          case 1: // Warning
            console.warn('[MSAL]', message);
            break;
          case 2: // Info
            console.info('[MSAL]', message);
            break;
          case 3: // Verbose
            console.debug('[MSAL]', message);
            break;
        }
      },
    },
  },
};

/**
 * Scopes for backend API access.
 *
 * 🛑 Spec 277 (owner 2026-09-12) — FCM MODEL: request ONLY the standard OIDC scopes and send the
 *    ID TOKEN, not a custom-`api://` access token. A personal Microsoft account (or a foreign
 *    tenant) CANNOT acquire `api://{clientId}/...` unless the resource app is installed/consented
 *    in that tenant first (AADSTS500011 / a consent loop) — which is exactly why FCP authenticated
 *    pdobler@doblerdata.com at Microsoft and then bounced it to the sign-out screen. OIDC scopes
 *    exist in every tenant, are user-consentable, and need no per-tenant install; the backend
 *    validates the ID token, whose `aud` is this app's clientId (already an accepted audience).
 *    Matches FabricControlMgmt `ClientAuthProvider` `SCOPES`. The token SENT to the API is
 *    `result.idToken` everywhere (AuthProvider / apiClient / the refresh hooks), not accessToken.
 */
const clientId = import.meta.env.VITE_AZURE_AD_CLIENT_ID || import.meta.env.VITE_ENTRA_CLIENT_ID;
export const apiTokenRequest = {
  scopes: ['openid', 'profile', 'email'],
};

/**
 * Scopes requested during login — same OIDC set (see apiTokenRequest above).
 */
export const loginRequest = {
  scopes: ['openid', 'profile', 'email'],
};

/**
 * Create MSAL instance
 */
export const msalInstance = new PublicClientApplication(msalConfig);

/**
 * Initialize MSAL
 * Must be called before rendering the app
 */
export async function initializeMsal(): Promise<void> {
  await msalInstance.initialize();
  
  console.info('[MSAL] Initialized. URL:', window.location.href);
  console.info('[MSAL] Hash present:', !!window.location.hash, 'Search:', window.location.search);
  
  // Handle redirect promise (processes auth code from Entra ID callback)
  try {
    const response = await msalInstance.handleRedirectPromise();
    if (response) {
      // Successfully authenticated via redirect — set active account
      msalInstance.setActiveAccount(response.account);
      console.info('[MSAL] Redirect login successful for', response.account?.username);
    } else {
      console.info('[MSAL] handleRedirectPromise returned null (no redirect to process)');
      console.info('[MSAL] Existing accounts:', msalInstance.getAllAccounts().length);
    }
  } catch (error: any) {
    if (error?.errorCode === 'no_token_request_cache_error') {
      // Stale redirect state — clear it and let the user log in fresh
      console.warn('[MSAL] Stale redirect state detected, clearing cache');
      sessionStorage.clear();
      if (window.location.hash) {
        window.history.replaceState(null, '', window.location.pathname);
      }
    } else {
      console.error('[MSAL] handleRedirectPromise error:', error);
    }
  }
}
